import supabase from './supabase';

/**
 * Generate a 6-digit verification code
 */
export const generateVerificationCode = () => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

/**
 * Send security verification email
 */
export const sendSecurityVerificationEmail = async (email, fullName, userRole) => {
  try {
    const verificationCode = generateVerificationCode();

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-security-verification-email`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token || ''}`,
        },
        body: JSON.stringify({
          email,
          verificationCode,
          fullName,
          userRole,
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send verification email');
    }

    const result = await response.json();

    // Store verification code in memory (frontend only, for this session)
    // In production, you'd want to store this server-side
    sessionStorage.setItem(
      `security_verification_${email}`,
      JSON.stringify({
        code: verificationCode,
        expiresAt: Date.now() + 10 * 60 * 1000, // 10 minutes
      })
    );

    return {
      success: true,
      message: 'Verification code sent to your email',
      data: result,
    };
  } catch (error) {
    console.error('Error sending verification email:', error);
    return {
      success: false,
      error: error.message || 'Failed to send verification email',
    };
  }
};

/**
 * Verify the code entered by user
 */
export const verifyEmailCode = (email, enteredCode) => {
  try {
    const storedData = sessionStorage.getItem(`security_verification_${email}`);

    if (!storedData) {
      return {
        success: false,
        error: 'No verification code found. Please request a new one.',
      };
    }

    const { code, expiresAt } = JSON.parse(storedData);

    // Check if code has expired
    if (Date.now() > expiresAt) {
      sessionStorage.removeItem(`security_verification_${email}`);
      return {
        success: false,
        error: 'Verification code has expired. Please request a new one.',
      };
    }

    // Check if code matches
    if (enteredCode !== code) {
      return {
        success: false,
        error: 'Invalid verification code. Please try again.',
      };
    }

    // Code is valid - clear it from storage
    sessionStorage.removeItem(`security_verification_${email}`);

    return {
      success: true,
      message: 'Email verified successfully',
    };
  } catch (error) {
    console.error('Error verifying email code:', error);
    return {
      success: false,
      error: 'An error occurred while verifying the code',
    };
  }
};

/**
 * Clear verification code from storage
 */
export const clearVerificationCode = (email) => {
  sessionStorage.removeItem(`security_verification_${email}`);
};
